import { Component, OnInit } from '@angular/core';
import { ApiService } from 'src/app/services/api.service';
import { ApiUrl } from 'src/app/services/apiUrl';
import { InvoiceImportService } from 'src/app/services/invoice-import.service';
import { InvoiceImportSummary } from 'src/app/models/invoice-import.model';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import Swal from 'sweetalert2';
import { TranslateService } from '@ngx-translate/core';
import {
  resolutionNoteKey,
  storeResolutionKey,
  translateInstant,
} from 'src/app/services/invoice-import.util';

@Component({
  selector: 'app-unassigned-invoice-imports',
  templateUrl: './unassigned-invoice-imports.component.html',
  styleUrls: ['./unassigned-invoice-imports.component.scss'],
})
export class UnassignedInvoiceImportsComponent implements OnInit {
  items: InvoiceImportSummary[] = [];
  total = 0;
  page = 1;
  limit = 20;
  loadError = '';
  stores: any[] = [];
  role = '';

  assignImportId: string | null = null;
  selectedStoreId = '';

  Math = Math;

  constructor(
    private invoiceImportService: InvoiceImportService,
    private api: ApiService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.role = localStorage.getItem('role') || '';
    if (this.role === 'clerk' || this.role === 'receipt_auditor') {
      this.loadError = this.translate.instant('INVOICE_IMPORTS.UNASSIGNED.FORBIDDEN');
      return;
    }
    this.loadStores();
    this.loadList();
  }

  loadList(): void {
    this.loadError = '';
    this.spinner.show();
    this.invoiceImportService.listUnassigned(this.page, this.limit).subscribe({
      next: (res: any) => {
        const data = res.response?.data || res.data || {};
        this.items = data.items || [];
        this.total = data.total || 0;
        this.spinner.hide();
      },
      error: (err: any) => {
        this.spinner.hide();
        this.loadError = err.error?.response?.message || err.message;
      },
    });
  }

  loadStores(): void {
    const managerId = localStorage.getItem('managerId');
    let endpoint = ApiUrl.storeListAdminDropdown;
    if (this.role === 'manager' && managerId) {
      endpoint = `${ApiUrl.storeListManagerDropdown}${managerId}`;
    }
    this.api.getRequest(endpoint).subscribe({
      next: (res: any) => {
        const responseData = res.response || res;
        if (this.role === 'manager') {
          this.stores = responseData.data || [];
        } else if (responseData.data?.storesData) {
          this.stores = responseData.data.storesData;
        } else {
          this.stores = responseData.data || [];
        }
      },
      error: () => {},
    });
  }

  openAssign(row: InvoiceImportSummary): void {
    this.assignImportId = row._id;
    this.selectedStoreId = '';
  }

  cancelAssign(): void {
    this.assignImportId = null;
  }

  confirmAssign(): void {
    if (!this.assignImportId || !this.selectedStoreId) {
      return;
    }
    this.spinner.show();
    this.invoiceImportService.assignStore(this.assignImportId, this.selectedStoreId).subscribe({
      next: () => {
        this.spinner.hide();
        this.toastr.success(this.translate.instant('INVOICE_IMPORTS.UNASSIGNED.ASSIGNED'));
        this.assignImportId = null;
        this.loadList();
      },
      error: (err: any) => {
        this.spinner.hide();
        Swal.fire('Error', err.error?.response?.message || err.message, 'error');
      },
    });
  }

  onPageChange(p: number): void {
    this.page = p;
    this.loadList();
  }

  identifiers(row: InvoiceImportSummary): string {
    const ids = row.parsedStoreIdentifiers || {};
    return Object.entries(ids)
      .filter(([k, v]) => k !== 'subAdvice' && (typeof v === 'string' || typeof v === 'number'))
      .map(([k, v]) => `${k}: ${v}`)
      .join(' · ');
  }

  storeResolutionLabel(row: InvoiceImportSummary): string {
    if (!row.storeResolution) {
      return '—';
    }
    return translateInstant(this.translate, storeResolutionKey(row.storeResolution));
  }

  resolutionNoteLabel(row: InvoiceImportSummary): string {
    if (!row.resolutionNote) {
      return '—';
    }
    return translateInstant(this.translate, resolutionNoteKey(row.resolutionNote));
  }
}
